import { config } from '../core/config';
// What this device keeps for desyr ID (36-desyr-id.md, Data): the signed-in desyr ID, the sign-ins
// under way by their state, the circles still to sign in silently, and the page to come back to
// from a desyr ID page; the circles being left and what to do after a reload (39). Never a password.
import { readJson, writeJson } from '../core/storage';

export interface DesyrId {
  issuer: string;
  sub: string;
  email: string;
  name?: string;
  /** The sign-in used a passkey (`amr` holds "mfa"), and when it was (seconds). */
  passkey: boolean;
  authTime?: number;
  refreshToken?: string;
  idToken: string;
  /** When the ID token ends (ms). */
  idUntil: number;
  accessToken?: string;
  accessUntil?: number;
}

const ID = 'desyr_id';
const PENDING = 'desyr_id_pending';
const QUEUE = 'desyr_id_queue';
const RETURN = 'desyr_id_return';
const ACTION = 'desyr_id_pending_action';

export const readId = () => readJson<DesyrId>(ID);
/** Signed in with desyr ID on this device (and the config names it). */
export const withId = () => !!config().desyrId && !!readId();
/** The stored sign-in is within the 15 minutes deleting needs (39, Behaviour 3.1). */
export const recentSignIn = (id: DesyrId | undefined, now = Date.now()) => !!id?.authTime && now - id.authTime * 1000 <= 15 * 60_000;
export const keepId = (id: DesyrId | undefined) => writeJson(ID, id);

/** A kept value that ends after a while (ms). */
type Timed<T> = { value: T; until: number };
const live = <T>(t: Timed<T> | undefined) => (t && t.until > Date.now() ? t.value : undefined);

export interface Pending {
  verifier: string;
  nonce: string;
  /** The page to come back to. */
  page: string;
  /** The sign-in generation at its start (Behaviour 4.4, v1.58.63). */
  gen?: number;
}
/** Per sign-in state, 10 minutes; expired ones are dropped whenever one is added or taken. */
/** Whether it could be kept (read back; storage blocked or full: false, 36 Starting signed out 2e). */
export function keepPending(state: string, pending: Pending): boolean {
  const all = readJson<Record<string, Timed<Pending>>>(PENDING) ?? {};
  writeJson(PENDING, { ...Object.fromEntries(Object.entries(all).filter(([, t]) => live(t))), [state]: { value: pending, until: Date.now() + 10 * 60_000 } });
  return !!readJson<Record<string, Timed<Pending>>>(PENDING)?.[state];
}
export function takePending(state: string): Pending | undefined {
  const all = readJson<Record<string, Timed<Pending>>>(PENDING) ?? {};
  const found = live(all[state]);
  delete all[state];
  writeJson(PENDING, Object.fromEntries(Object.entries(all).filter(([, t]) => live(t))));
  return found;
}

/** The circles (server names) still to sign in silently, 10 minutes. */
export const readQueue = () => live(readJson<Timed<string[]>>(QUEUE)) ?? [];
export const keepQueue = (servers: string[]) => writeJson(QUEUE, servers.length ? { value: servers, until: Date.now() + 10 * 60_000 } : undefined);

/** The page to come back to from a desyr ID page, 30 minutes; taken once. */
export const keepReturn = (page: string) => writeJson(RETURN, { value: page, until: Date.now() + 30 * 60_000 });
export function takeReturn(): string | undefined {
  const page = live(readJson<Timed<string>>(RETURN));
  writeJson(RETURN, undefined);
  return page;
}

const LEAVING = 'desyr_id_leaving';
/**
 * Circles being left (39, Behaviour 1.6): server name → until (ms), 30 minutes. `listed`: the
 * servers the list holds now; one it no longer holds, or past its time, is forgotten. The rest.
 */
export function leaving(listed?: string[], add?: string): string[] {
  const now = Date.now();
  const all = Object.entries(readJson<Record<string, number>>(LEAVING) ?? {}).filter(([s, until]) => until > now && (!listed || listed.includes(s)));
  if (add) all.push([add, now + 30 * 60_000]);
  writeJson(LEAVING, all.length ? Object.fromEntries(all) : undefined);
  return all.map(([s]) => s);
}

/**
 * Signing out everywhere (36, Behaviour 4.2), from the moment the call is sent (the services end this
 * device's sessions before they answer): this page's circles fail silently until OK signs the device out.
 */
let everywhere = false;
export const signedOutEverywhere = (now?: boolean) => (now === undefined ? everywhere : (everywhere = now));

/** Joining a circle again ends its leaving mark, so it shows and signs in at once. */
export function stopLeaving(server: string): void {
  const rest = Object.entries(readJson<Record<string, number>>(LEAVING) ?? {}).filter(([s]) => s !== server);
  writeJson(LEAVING, rest.length ? Object.fromEntries(rest) : undefined);
}

const AFTER = 'desyr_id_after';
/**
 * What to do once the page has loaded again (39, Data; this tab): a dialog to open again after
 * signing in to desyr ID, or a toast or note to show; or the circle a switch goes on to (36, Behaviour 6.4).
 */
export type After = { do: 'leave'; slug: string } | { do: 'delete' } | { do: 'left' | 'removed'; name: string } | { do: 'deleted' } | { do: 'joined'; name: string; mxid: string } | { do: 'switch'; mxid: string };
export const keepAfter = (after: After) => writeJson(AFTER, after, sessionStorage);
export const readAfter = () => readJson<After>(AFTER, sessionStorage);
/** Read once: taken only when it is one of `kinds`. */
export function takeAfter<K extends After['do']>(...kinds: K[]): Extract<After, { do: K }> | undefined {
  const after = readAfter();
  if (!after || !kinds.includes(after.do as K)) return undefined;
  writeJson(AFTER, undefined, sessionStorage);
  return after as Extract<After, { do: K }>;
}
/** A leave or a delete waits for its dialog (39, Behaviour 1.7): the sign-in lands there, never on "Your circles" first. */
export const actionWaiting = () => ['leave', 'delete'].includes(readAfter()?.do ?? '');
/** A change waiting for the passkey sign-in again (37, Behaviour 3.1): the settings page, the dialog and its typed fields (no secrets), and when it was kept (ms); 5 minutes. */
export interface PendingAction {
  page: string;
  dialog?: Record<string, string>;
  at: number;
}
export const keepAction = (action: PendingAction) => writeJson(ACTION, { value: action, until: Date.now() + 5 * 60_000 });
export const readAction = () => live(readJson<Timed<PendingAction>>(ACTION));
export const dropAction = () => writeJson(ACTION, undefined);

/**
 * Starting signed out (36, v1.58.51; v1.58.63): `desyr_signed_out` after signing out on this device or a
 * session ended elsewhere, until the next completed sign-in: JSON `{ at, ended, state }`, `ended` once
 * desyr ID's end-session came back with that `state` (Behaviour 4.3; "1" from before reads as not ended).
 * While it is there no start jumps and every sign-in asks again (`prompt=login`).
 */
export const SIGNED_OUT = 'desyr_signed_out';
const AUTO = 'desyr_id_auto';
const mark = (key: string, value?: string) => {
  try {
    if (value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // storage refused: the card shows
  }
};
export interface SignedOut {
  at: number;
  ended: boolean;
  state?: string;
}
export function signedOutMark(): SignedOut | undefined {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(SIGNED_OUT);
  } catch {
    // storage refused: no mark
  }
  if (raw === null) return undefined;
  const m = readJson<Partial<SignedOut> | number>(SIGNED_OUT);
  const o = typeof m === 'object' && m ? m : {};
  return { at: Number(o.at) || 0, ended: o.ended === true, ...(typeof o.state === 'string' ? { state: o.state } : {}) };
}
/** Signed out here, not (yet) confirmed by desyr ID; `state`: the end-session request's. */
export const markSignedOut = (state?: string) => mark(SIGNED_OUT, JSON.stringify({ at: Date.now(), ended: false, ...(state ? { state } : {}) }));
/** Back at `/login` from desyr ID's end-session with the mark's `state`: the end is confirmed (4.3). */
export function endConfirmed(state: string | null): boolean {
  const m = signedOutMark();
  if (!state || !m?.state || m.state !== state) return false;
  mark(SIGNED_OUT, JSON.stringify({ ...m, ended: true }));
  return true;
}
export const signedInDone = () => (mark(SIGNED_OUT), mark(AUTO));
/** Continue with desyr ID, Try again or Sign in again pressed: the person signs in, not a jump (2d, v1.58.63). */
export const dropAuto = () => mark(AUTO);

/**
 * The loop guard (36, Starting signed out 2d, v1.58.63): one automatic jump, then the card, until a sign-in
 * completes or a button is pressed. Under the Web Lock `desyr_id_auto` (only if free) the key is looked
 * for, written and read back; a busy lock, a key found, the signed-out mark or storage that refuses: no jump.
 */
export async function claimJump(): Promise<boolean> {
  const claim = () => {
    try {
      if (localStorage.getItem(SIGNED_OUT) !== null || localStorage.getItem(AUTO) !== null) return false;
      const at = String(Date.now());
      localStorage.setItem(AUTO, at);
      return localStorage.getItem(AUTO) === at;
    } catch {
      return false;
    }
  };
  if (!navigator.locks) return claim();
  return navigator.locks.request(AUTO, { ifAvailable: true }, (lock) => !!lock && claim()).catch(() => false);
}

/**
 * The sign-in generation (36, Behaviour 4.4, v1.58.63): `desyr_auth_gen`, moved on by every sign-out; a
 * sign-in, renewal, sessions call or hop started under an older one stores nothing.
 */
export const GEN = 'desyr_auth_gen';
export function authGen(): number {
  try {
    return Number(localStorage.getItem(GEN)) || 0;
  } catch {
    return 0;
  }
}
/** Every sign-in under way forgotten (4.4.1): its pending entries, the queue, the page to come back to. */
export function forgetUnderWay(): void {
  writeJson(PENDING, undefined);
  writeJson(QUEUE, undefined);
  writeJson(RETURN, undefined);
}
